import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import filter from 'lodash/fp/filter';
import flatten from 'lodash/fp/flatten';
import sortBy from 'lodash/fp/sortBy';
import groupBy from 'lodash/fp/groupBy';
import uniqBy from 'lodash/fp/uniqBy';
import zipAll from 'lodash/fp/zipAll';

import { Tile } from '../../../../tiles/slice/types';
import {
  Suggestion,
  BuildingSuggestion,
  AdMarketplaceData,
  AdMarketplacePaid,
  AdMarketplaceOrganic,
  PopularWebsite,
  HistoryPage,
  SearchTag,
  Engines,
  Zeropark,
  ZeroparkBrand,
  AdMarketplaceLongTailData,
} from '../../types';
import { FeatureFlags } from '../../../../user/slice/types';

import {
  checkIsUrl,
  withHttp,
  createTermFromUrl,
  urlPrettifiers,
  fullTextMatch,
  matchUrl,
} from '../../utils';

import { ENGINES } from '../../../utils';

// -----------------------------------------------------------------------------

export const calculateMatchPriority =
  (q: string) =>
    (s: BuildingSuggestion): [number, string] => {
      const url = s.displayUrl || s.url;
      if (url && matchUrl(url)(q)) return [1, url];
      const term = (s.term || '').toLowerCase();
      if (term) {
        if (q.length >= 2 && term.startsWith(q)) return [2, q];
        if (q.length >= 4 && fullTextMatch(q)(term)) return [3, q];
      }

      return [-1, ''];
    };

const mapMatchPriority =
  (q) =>
    (s: BuildingSuggestion): BuildingSuggestion => {
      const priority = calculateMatchPriority(q)(s)[0];
      return { ...s, priority };
    };

const filterPriority = (s: BuildingSuggestion) => s.priority >= 0;

// const filterMatch = (q) => ({ term, url }: { term?: string, url?: string }) => {
//   if (q.length >= 4 && term && fullTextMatch(q)(term)) return true;
//   return matchUrl(url)(q);
// }

export const preparePriorityOrder = (q: string) =>
  flow(map(mapMatchPriority(q)), filter(filterPriority));

// -----------------------------------------------------------------------------

export const formatHistory = (
  q: string,
  engine: Engines,
  isOnlySERP: boolean
): ((list: HistoryPage[]) => BuildingSuggestion[]) =>
  flow(
    (list = []) => {
      if (!q) return list;

      return filter(
        (page: HistoryPage) =>
          calculateMatchPriority(q)({ url: page.url, term: page.title })[0] >= 0
      )(list);
    },
    map((page: HistoryPage) => {
      const usedEngine = Object.values(ENGINES).find(({ url }) =>
        page.url.match(new URL(url).host)
      );
      if (usedEngine) {
        const term = new URLSearchParams(page.url.split('?')[1]).get(
          usedEngine.q
        );
        if (term) {
          return createOrganicSuggestion(
            { id: page.url, term, type: 'historyOrganic' },
            engine
          );
        } else {
          return null;
        }
      }

      if (isOnlySERP) return null;

      return {
        id: page.url,
        type: 'history',

        term: page.title || createTermFromUrl(page.url),
        url: page.url,
        favIconUrl: (page.favIconUrl || '').match('https://')
          ? page.favIconUrl
          : getFaviconUrl(page.url),
      };
    }),
    filter(Boolean),
    (list) => {
      if (!q) {
        return flow(
          uniqBy((s: BuildingSuggestion) => {
            try {
              if (s.type === 'history' && s.url) {
                return new URL(s.url).host;
              }
            } catch (err) {
              console.error('uniqBy', err);
            }

            return s.url;
          }),
          (l) => l.slice(0, 10)
        )(list);
      }

      return filter((s: BuildingSuggestion) =>
        s.type === 'organic'
          ? calculateMatchPriority(q)({ term: s.term })[0] >= 0
          : true
      )(list);
    }
  );

// -----------------------------------------------------------------------------

export const formatZeropark =
  (q: string) =>
    (zp: Zeropark): Suggestion[] => {
      if (!q || q.length < 3 || !zp?.brands?.length) return [];

      try {
        const term = q.toLowerCase();
        return flow(
          map((brand: ZeroparkBrand) => {
            const found = [
              brand.brand.url,
              brand.brand.name,
              ...brand.keywords,
            ].find((v) => {
              const vv = v.toLowerCase();
              if (vv.toLowerCase().startsWith(term)) return true;
              if (term.length > 4) return vv.includes(term) || term.includes(vv);
              return false;
            });

            return found
              ? {
                type: 'ad',
                id: brand.brand.url,
                term: found === brand.brand.url ? brand.brand.name : found,
                displayUrl: brand.brand.url,
                url: zp.clickUrl
                  .replace(/\${brand.url}/g, brand.brand.url)
                  .replace(/\${brand.name}/g, brand.brand.name),
                favIconUrl: createFaviconUrl(brand.brand.url),
                priority: found.startsWith(term) ? 1 : 2,
                provider: 'zeropark',
                revenue: brand.eCpc,
              }
              : null;
          }),
          filter(Boolean),
          sortBy('priority'),
          (list: Suggestion[]) => list.slice(0, 2)
        )(zp.brands);
      } catch (err) {
        // TODO: track it
        console.error('formatZeropark', err);
        return [];
      }
    };

// -----------------------------------------------------------------------------

const createUrlSuggestion = (s: BuildingSuggestion): BuildingSuggestion => {
  const url = withHttp(s.url || s.term);
  return {
    ...s,
    url,
    type: s.type || 'url',
    term: s.term || createTermFromUrl(url),
    favIconUrl: getFaviconUrl(url),
  };
};

export const createOrganicUrl = (
  s: BuildingSuggestion,
  engine: Engines
): string => {
  const TARGET_BROWSER = process.env.TARGET_BROWSER || 'chrome';
  const { url, q } = ENGINES[engine] || {};

  const searchParams = new URLSearchParams({
    ...s.query,
    [q]: s.term,
    ...(['oh', 'bing'].includes(engine) && {
      extensionVersion: process.env.APP_VERSION,
      utm_source:
        TARGET_BROWSER.charAt(0).toUpperCase() + TARGET_BROWSER.slice(1),
      utm_medium: 'Extension:ntp',
    }),
  });

  return `${url}?${searchParams}`;
};

export const createOrganicSuggestion = (
  s: BuildingSuggestion,
  engine: Engines
): BuildingSuggestion => ({
  ...s,
  type: s.type || 'organic',
  url: createOrganicUrl(s, engine),
});

export const decodeTerm = (s: BuildingSuggestion): BuildingSuggestion => {
  const term = s.term || '';
  if (term.startsWith('http:') || term.startsWith('https:')) {
    try {
      return { ...s, term: new URL(term).toString() };
    } catch (err) {
      return s;
    }
  }

  return s;
};

export const createFormatOrganic = (
  q: string,
  engine: Engines
): ((list: string[] | BuildingSuggestion[]) => Suggestion[]) =>
  flow(
    map((s) => (typeof s === 'string' ? { term: s } : s)),
    map(decodeTerm),
    map(
      (s: BuildingSuggestion): BuildingSuggestion =>
        checkIsUrl(s.term)
          ? createUrlSuggestion({ ...s, url: s.term })
          : createOrganicSuggestion(s, engine)
    ),
    (list) => [
      createOrganicSuggestion({ term: q, type: 'organicProvided' }, engine),
      ...list,
    ],
    (list: BuildingSuggestion[]): Suggestion[] => {
      if (!list.length) {
        return [createOrganicSuggestion({ term: q }, engine) as Suggestion];
      }

      if (!checkIsUrl(q)) return list as Suggestion[];
      if (list[0]?.type === 'url') return list as Suggestion[];
      return [
        createUrlSuggestion({ term: q, type: 'urlCandidate' }),
        createOrganicSuggestion({ term: q }, engine),
        ...list,
      ] as Suggestion[];
    }
  );

export const createSearchTagSuggestion =
  (searchTag: SearchTag) =>
    (s: Suggestion): Suggestion => {
      const sTerm = s.term.toLowerCase();
      const tagTerm = searchTag.s.toLowerCase();
      const term = (
        sTerm.startsWith(tagTerm) ? s.term.substr(tagTerm.length) : s.term
      ).trim();
      const url = searchTag.u.replace('{{{s}}}', encodeURI(term));

      return {
        ...s,
        term,
        url,
        type: 'searchTag',
        favIconUrl: getFaviconUrl(url),
      };
    };

// -----------------------------------------------------------------------------

const formatAdmarketplaceAd = (s: AdMarketplacePaid): BuildingSuggestion => ({
  type: 'ad',
  provider: 'adMarketplace',
  revenue: 0.5,
  term: s.term,
  url: s.click_url,
  favIconUrl: s.image_url,
  impressionUrl: s.impression_url,
  displayUrl: s.brand ? s.brandDomain : null,
});

export const createFormatAdMarketplace =
  (q: string, engine: Engines) =>
    (data: AdMarketplaceData): Suggestion[] => {
      // console.log('data', data);

      const organic: AdMarketplaceOrganic[] = Array.isArray(
        data?.organic_suggestions
      )
        ? data.organic_suggestions
        : [];
      const paid: AdMarketplacePaid[] = Array.isArray(data?.paid_suggestions)
        ? data.paid_suggestions
        : [];

      return flow(
        map((s: AdMarketplaceOrganic) =>
          s.paid_suggestion_ids
            ? s.paid_suggestion_ids.map((id) => paid.find((p) => p.id === id))
            : s
        ),
        flatten,
        filter(Boolean),
        groupBy(({ impression_url }) => (impression_url ? 'ad' : 'organic')),
        (groups: {
          ad: AdMarketplacePaid[];
          organic: AdMarketplaceOrganic[];
        }) => {
          const ads = groups.ad || [];
          const organic = (groups.organic || []).map((s) => s.term);

          return [
            ...ads.map(formatAdmarketplaceAd),
            ...createFormatOrganic(q, engine)(organic),
          ];
        }
      )(organic);
    };

export const createFormatAdMarketplaceLongtail =
  (q: string, engine: Engines) =>
    ([data, organic]: [
      AdMarketplaceLongTailData,
      string[] | BuildingSuggestion[]
    ]): Suggestion[] => {
      const organicList = createFormatOrganic(q, engine)(organic);

      if (!data.count) return organicList;

      const ads = data.adlistings.listing.map(
        (listining): Suggestion => ({
          id: listining.adId,
          url: listining.clickurl,
          displayUrl: listining.displayurl,
          impressionUrl: listining.impressionurl,
          term: listining.title,
          // label: listining.description,
          type: 'ad',

          favIconUrl: getFaviconUrl(withHttp(listining.displayurl)),
          provider: 'adMarketplace_search',
          revenue: 0.5,
        })
      );

      return [...ads, ...organicList];
    };

// -----------------------------------------------------------------------------

export const formatTiles = (tile: Tile): Suggestion => ({
  id: tile.url,
  term: tile.title || createTermFromUrl(tile.url),
  url: tile.url,
  favIconUrl: getFaviconUrl(tile.url),
  type: 'tile',
});

// -----------------------------------------------------------------------------

export const formatPopularWebsites = (
  q: string
): ((list: PopularWebsite[]) => Suggestion[]) =>
  flow(
    preparePriorityOrder(q),
    map((s: PopularWebsite) => ({ ...s, type: 'website' })),
    map(createUrlSuggestion)
  );

// -----------------------------------------------------------------------------

const ORDER = [
  'url',
  'website',
  'ad',
  'history',
  'historyOrganic',
  'tile',
  'website',
  'url',
  'urlCandidate',
  'organicProvided',
  'organic',
  'searchTag',
  'openTab',
];

const sortSuggestions =
  (q: string) =>
    (s: BuildingSuggestion): number => {
      if (s.type === 'ad') return 0;

      const priority = calculateMatchPriority(q)(s);
      const isOrganic = [
        'organic',
        'ad',
        'organicProvided',
        'searchTag',
      ].includes(s.type);
      // const isOrganic = ['organic', 'organicProvided', 'searchTag'].includes(s.type);

      const result = [
        (() => {
          if (isOrganic) return 2;
          return priority[0] === -1 ? 3 : priority[0];
        })(),
        ORDER.indexOf(s.type),
        // shorter higher priority within it's group, ignore organic & organicProvider
        !priority[1] || isOrganic ? '00' : `0${priority[1].length}`.substr(-2),
      ].join('');

      return Number(result);
    };

const prettifyUrl = flow(
  urlPrettifiers.removePrefix,
  urlPrettifiers.removeSuffix
);

let $input;
export const getTotalLimit = (): number => {
  if (typeof window === 'undefined') return 10;

  if (typeof document !== 'undefined') {
    $input = $input || document.querySelector('[name="q"]');
  }

  const { y } =
    typeof document === 'undefined'
      ? { y: 330 }
      : $input?.getBoundingClientRect() || { y: 330 };

  const dimensions = { suggestion: 40, inputFromTop: y };
  return Math.floor(
    (window.innerHeight - dimensions.inputFromTop - 50) / dimensions.suggestion
  );
};

const limitSuggestions = (q: string) => (list) => {
  const limit = getTotalLimit();

  if (!q && (list.length <= limit || !limit)) return list;

  const groups = groupBy('type')(list);

  const max2Categories = [
    'history',
    'historyOrganic',
    'tile',
    'website',
    'openTab',
  ];

  const specialCategories = flow(
    map((type: string) => (groups[type] || []).slice(0, 2)),
    zipAll,
    flatten,
    filter(Boolean),
    (list) => list.slice(0, 4)
  )(max2Categories);

  return list
    .filter((s: BuildingSuggestion) =>
      max2Categories.includes(s.type) ? specialCategories.includes(s) : true
    )
    .slice(0, limit);
};

const filterBlacklist = (featureFlags) => {
  const adBlacklist = featureFlags.AD_AUTOSUGGESTION_BLACKLIST || [];

  if (!adBlacklist.length) {
    return (list) => list;
  }

  return filter(
    (s: BuildingSuggestion) =>
      !adBlacklist.find(([key, value]) => s[key] === value)
  );
};

export const mapId = (s: BuildingSuggestion): Suggestion =>
  s.id ? (s as Suggestion) : ({ ...s, id: s.id || s.url } as Suggestion);

const getHost = (url) => {
  try {
    return new URL(url).host;
  } catch (err) {
    return url;
  }
};

export const formatFinalData = (
  q: string,
  featureFlags: FeatureFlags,
  list: Suggestion[]
): Suggestion[] =>
  flow(
    filterBlacklist(featureFlags),
    map(mapId),
    sortBy(sortSuggestions(q)),
    uniqBy((s: BuildingSuggestion) => prettifyUrl(s.displayUrl || s.url)),
    uniqBy(
      (s: BuildingSuggestion) =>
        `${(s.term || '').toLowerCase().trim()}:${getHost(s.url)}`
    ),
    limitSuggestions(q)
  )(list);

const calendarFav =
  'https://calendar.google.com/googlecalendar/images/favicons_2020q4/calendar_3.ico';
const faviconMap = {
  'https://calendar.google.com': calendarFav,
  '/calendar/b/0/render': calendarFav,
  'https://www.google.com/calendar': calendarFav,

  'https://drive.google.com':
    'https://ssl.gstatic.com/images/branding/product/1x/drive_2020q4_32dp.png',
  'https://mail.google.com':
    'https://ssl.gstatic.com/ui/v1/icons/mail/rfr/gmail.ico',
};

export const createFaviconUrl = (domain: string, sz = '32'): string =>
  `https://www.google.com/s2/favicons?${new URLSearchParams({
    domain,
    sz,
  })}`;

export const getFaviconUrl = (url: string, sz = '32'): string => {
  try {
    const { origin, pathname } = new URL(url);
    return (
      faviconMap[origin] ||
      faviconMap[pathname] ||
      faviconMap[`${origin}${pathname}`] ||
      createFaviconUrl(new URL(url).origin, sz)
    );
  } catch (err) {
    return null;
  }
};
